import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DownCaretIcon, TextInput, WarningIcon } from '@streamr/tv-ui';
import { addDays } from 'date-fns';
import pick from 'lodash/pick';
import { useCallback, useEffect, useId, useMemo, useRef, useState, } from 'react';
import styled from 'styled-components';
import { generateKeysAndUserId } from '../../engine/helpers/generateKeysAndUserId';
import { generateBroadcastId, validateScheduledBroadcast, } from '../../services/schedule/ScheduledBroadcast';
import { ScheduleService } from '../../services/schedule/ScheduleService';
import { Popup } from '../../utils/popups';
import { formatDate } from '../../utils/time';
import { generateStreamId } from '../../utils/url';
import { BroadcastNameplate } from '../BroadcastNameplate';
import Label from '../Label';
import { PopupButton } from '../PopupButton';
import { DismissalReason } from './AbstractPopup';
import { showCalendar } from './CalendarPopup';
import { FormPopup } from './FormPopup';
export function ScheduleBroadcastPopup({ broadcast, reject: rejectProp, resolve, }) {
    const baseBroadcast = useMemo(() => {
        if (broadcast !== undefined) {
            return pick(broadcast, 'id', 'streamId', 'broadcasterPrivateKey', 'broadcasterUserId', 'aesKey');
        }
        const streamId = generateStreamId();
        const { privateKey, userId, aesKey } = generateKeysAndUserId();
        return {
            id: generateBroadcastId(),
            streamId,
            broadcasterPrivateKey: privateKey,
            broadcasterUserId: userId,
            aesKey,
        };
    }, [broadcast]);
    const [name, setName] = useState(broadcast?.name || '');
    const startsAt = broadcast?.startsAt;
    const [nextStartsAt, setNextStartsAt] = useState(startsAt);
    const nextLocalDate = useMemo(() => new Date(nextStartsAt || 0), [nextStartsAt]);
    const [time, setTime] = useState('');
    const localDateTimeString = `${formatDate(nextLocalDate, 'yyyy-MM-dd')}T${time}`;
    const [isDirty, setIsDirty] = useState(false);
    useEffect(() => {
        const defaultDate = addDays(Date.now(), 1);
        setNextStartsAt(startsAt ?? defaultDate.getTime());
        setTime(formatDate(startsAt || defaultDate, 'HH:mm'));
    }, [startsAt]);
    const validationErrors = useMemo(() => {
        return validateScheduledBroadcast({
            name,
            startsAt: new Date(localDateTimeString).getTime(),
            ...baseBroadcast,
        });
    }, [name, localDateTimeString, baseBroadcast]);
    const isValid = validationErrors.length === 0;
    const nameError = useMemo(() => {
        if (!isDirty)
            return null;
        const nameErrors = validationErrors.filter((issue) => issue.path[0] === 'name');
        if (nameErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = nameErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return `The title can not be shorter than ${error.minimum} characters.`;
            case 'too_big':
                return `The title can not be longer than ${error.maximum} characters.`;
            default:
                return 'The title is required';
        }
    }, [validationErrors, isDirty]);
    const dateError = useMemo(() => {
        if (!isDirty)
            return null;
        const dateErrors = validationErrors.filter((issue) => issue.path[0] === 'startsAt');
        if (dateErrors.length === 0)
            return null;
        // Get the first error for the name field
        const error = dateErrors[0];
        switch (error.code) {
            case 'invalid_type':
                return `Invalid input type: expected ${error.expected}`;
            case 'too_small':
                return 'The date can not be in the past.';
            default:
                return 'The date is required';
        }
    }, [validationErrors, isDirty]);
    const scheduleService = ScheduleService.getNotificationInterface();
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const reject = useCallback((reason) => {
        if (!isCalendarOpen) {
            rejectProp(reason);
        }
    }, [rejectProp, isCalendarOpen]);
    const dateControlRef = useRef(null);
    const calendarPopupControllerRef = useRef(null);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `validationErrors` is just the trigger.
    useEffect(() => {
        calendarPopupControllerRef.current?.refreshAnchors();
    }, [validationErrors]);
    const nameInputId = useId();
    const timeInputId = useId();
    return (_jsx(FormPopup, { reject: reject, title: "Schedule broadcast", canSubmit: isValid, onSubmit: () => {
            setIsDirty(true);
            if (!isValid) {
                return;
            }
            const modifiedBroadcast = {
                name,
                startsAt: new Date(localDateTimeString).getTime(),
                ...baseBroadcast,
            };
            if (broadcast) {
                scheduleService.notifyUpdateScheduledBroadcast(modifiedBroadcast);
            }
            else {
                scheduleService.notifyAddScheduledBroadcast(modifiedBroadcast);
            }
            resolve();
        }, cancelButtonProps: {
            onClick: () => {
                reject(DismissalReason.CloseButton);
            },
        }, okButtonProps: {
            label: broadcast?.id ? 'Update' : 'Create',
        }, children: _jsxs(Body, { children: [_jsx(BroadcastNameplate, { broadcast: {
                        name,
                        startsAt: startsAt ?? Date.now(),
                        ...baseBroadcast,
                    } }), _jsxs("div", { children: [_jsx(Label, { htmlFor: nameInputId, children: "Event title" }), _jsx(TextInput, { id: nameInputId, placeholder: "Untitled event", value: name, onChange: (e) => {
                                setIsDirty(true);
                                setName(e.target.value);
                            }, invalid: isDirty && !!nameError }), isDirty && nameError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), nameError] }))] }), _jsxs("div", { children: [_jsx(Label, { htmlFor: "date", children: "Schedule" }), _jsxs(DateTimeControls, { children: [_jsx(Control, { ref: dateControlRef, children: _jsx(DateTimeButton, { type: "button", onClick: () => {
                                            showCalendar(nextLocalDate, {
                                                controllerRef: calendarPopupControllerRef,
                                                anchorRef: dateControlRef,
                                                onDateChange: (newDate) => {
                                                    setNextStartsAt(newDate.getTime());
                                                    setIsDirty(true);
                                                },
                                                onBeforeOpen: () => {
                                                    setIsCalendarOpen(true);
                                                },
                                                onAfterClose: () => {
                                                    setIsCalendarOpen(false);
                                                },
                                            });
                                        }, appendComponent: _jsx(DownCaretIcon, {}), children: formatDate(nextLocalDate, 'LLL d, yyyy') }) }), _jsx(Control, { children: _jsx(TextInput, { id: timeInputId, type: "time", value: time, onChange: (e) => {
                                            setIsDirty(true);
                                            setTime(e.target.value);
                                        } }) })] }), isDirty && dateError && (_jsxs(ErrorText, { children: [_jsx(WarningIcon, {}), dateError] }))] })] }) }));
}
const DateTimeButton = styled(PopupButton) `
    min-height: 0;
    height: 3.375rem;
    background-color: var(--tv-ui-color-14);
    box-shadow: 1px 1.5px 4px 0px #0000001A inset,
        1px 1.5px 4px 0px #00000014 inset,
        0px -0.5px 1px 0px #FFFFFF40 inset,
        0px -0.5px 1px 0px #FFFFFF4D inset;

    &:hover {
        background-color: var(--tv-ui-color-14);
        transition-duration: 50ms;
    }

    &:focus {
        box-shadow: 0px 1px 1px rgba(255, 255, 255, 0.3), inset 0px 2px 6px rgba(39, 39, 39, 0.4), 0 0 0 2px rgba(239, 239, 239, 0.3);
    }
`;
const Control = styled.div ``;
const DateTimeControls = styled.div `
    display: grid;
    gap: 0.375rem;

    @media (min-width: 25rem) {
        grid-template-columns: 1fr 1fr;
    }
`;
const Body = styled.div `
    display: grid;
    gap: 2rem;
    padding: 1.5rem;
    padding-top: 0;
`;
const ErrorText = styled.div `
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 0.25rem;
    font-weight: 500;
    font-size: 0.875rem;
    line-height: 0.875rem;
    color: #ffffff;
    margin-top: 0.25rem;
`;
const scheduleBroadcastPopup = Popup.create(ScheduleBroadcastPopup);
export function scheduleBroadcast(broadcast, options = {}) {
    (async () => {
        try {
            options.onOpenPopup?.();
            await scheduleBroadcastPopup.pop({ broadcast });
        }
        catch (_ignore) {
        }
        finally {
            options.onClosePopup?.();
        }
    })();
}
